// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@conklin/App/Store/Reducers";
import IconProdListingsZoom from "@conklin/XDL/Icons/IconProdListingsZoom";
import { getImageKitSrc } from "@conklin/XDL/Utilities";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { ImageModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon from "@insite/mobius/Icon";
import Img, { ImgProps } from "@insite/mobius/Img";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

// eslint-disable-next-line spire/fenced-imports
import { useElementSize } from "@conklin/App/Hooks/useSize";
import breakpoints from "@conklin/XDL/PreStyle/breakpoints";
import { EasyZoomOnHover, enableScroll } from "easy-magnify";

interface OwnProps {
    product: ProductModel;
    image: ImageModel;
    useLargeImage?: boolean;
    onClick?: () => void;
    extendedStyles?: ProductPrimaryImageStyles;
    // XNG-Change
    useZoom?: boolean;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: ApplicationState) => {
    const settings = getSettingsCollection(state) as any;
    return {
        productSettings: settings.productSettings,
        // XNG-Change
        isTabletScreen: (state as CustomAppState).xCommon?.isTabletScreen,
        notFoundImagePath:
            settings.catalogSettings?.notFoundSmallImagePath ||
            settings.catalogSettings?.notFoundMediumImagePath ||
            settings.catalogSettings?.notFoundLargeImagePath,
    };
};

export interface ProductPrimaryImageStyles {
    centeringWrapper?: InjectableCss;
    /**
     * @deprecated Not used anymore
     */
    hiddenWrapper?: InjectableCss;
    /**
     * @deprecated Not used anymore
     */
    spinner?: LoadingSpinnerProps;
    /**
     * @deprecated Use img instead
     */
    image?: LazyImageProps;
    img?: ImgProps;
    // XNG-Change
    zoomContainerStyles?: InjectableCss;
    container?: InjectableCss;
    enlargeBtn?: ClickablePresentationProps;
}

export const productPrimaryImageStyles: ProductPrimaryImageStyles = {
    centeringWrapper: {
        css: css`
            min-height: 300px;
            margin: 10px 0;
            display: flex;
            align-items: center;
            justify-content: center;
        `,
    },
    image: {
        css: css`
            margin: auto;
            cursor: pointer;
        `,
    },
    img: {
        css: css`
            cursor: pointer;
        `,
    },
};

const ProductPrimaryImage = ({
    productSettings,
    product,
    image,
    useLargeImage,
    onClick,
    extendedStyles,
    // XNG-Change
    isTabletScreen,
    useZoom,
    notFoundImagePath,
}: Props) => {
    const [styles] = useState(() => mergeToNew(productPrimaryImageStyles, extendedStyles));
    const documentWidth = useElementSize(typeof document !== "undefined" ? document.body : null)?.width;
    const [zoomContainer, setZoomContainer] = useState({ width: 884, height: 687 });
    if (!product) {
        return null;
    }

    useEffect(() => {
        if (documentWidth) {
            if (documentWidth >= breakpoints.maxWidths[3]) {
                setZoomContainer({ width: 884, height: 687 });
            } else {
                const newWidth = documentWidth - 484;
                setZoomContainer({ width: Math.floor(newWidth), height: Math.floor(newWidth * 0.77) });
            }
        }
    }, [documentWidth]);

    useEffect(() => {
        if ((window as any).sirvScriptAdded) {
            return;
        }

        if (
            productSettings.imageProvider !== "SIRV" ||
            !product.images ||
            product.images.every(o => o.imageType !== "360")
        ) {
            return;
        }

        const script = document.createElement("script");
        script.src = "https://scripts.sirv.com/sirv.js";
        script.async = true;

        document.body.appendChild(script);
        (window as any).sirvScriptAdded = true;
    }, [productSettings.imageProvider, product]);

    const imageWrapperClickHandler = () => {
        // XNG-Change
        // onClick?.();
    };

    // XNG-Change
    const viewImage = () => {
        onClick?.();
    };

    const imageWrapperKeyHandler: React.KeyboardEventHandler<HTMLImageElement> = event => {
        event.preventDefault();
        event.stopPropagation();
        if (event.key === "Enter") {
            onClick?.();
        }
    };

    const path = useLargeImage
        ? image.largeImagePath || image.mediumImagePath
        : image.mediumImagePath || image.largeImagePath;
    if (!path) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container} data-test-selector="primary-image-container">
            {image.imageType === "Static" && (
                <StyledWrapper {...styles.centeringWrapper}>
                    {useZoom && !isTabletScreen && path !== notFoundImagePath ? (
                        <StyledWrapper
                            {...styles.zoomContainerStyles}
                            {...{ width: zoomContainer.width }}
                            onMouseOver={() => enableScroll?.()}
                        >
                            <EasyZoomOnHover
                                key={`eazyzoom_${zoomContainer.width}_${zoomContainer.height}`}
                                mainImage={{
                                    src: getImageKitSrc(image.mediumImagePath, 346, 346),
                                    alt: image.imageAltText,
                                    width: 346,
                                    height: 346,
                                }}
                                zoomImage={{
                                    src: getImageKitSrc(image.mediumImagePath, 1038, 1038),
                                    alt: image.imageAltText,
                                }}
                                zoomContainerWidth={zoomContainer.width}
                                zoomContainerHeight={zoomContainer.height}
                                delayTimer={0}
                                distance={22}
                            />
                        </StyledWrapper>
                    ) : (
                        <Img
                            {...styles.img}
                            src={path}
                            altText={image.imageAltText || "product main image"}
                            data-test-selector="productDetails_mainImage"
                            onKeyPress={imageWrapperKeyHandler}
                            tabIndex={0}
                            width={350}
                            height={350}
                        />
                    )}
                </StyledWrapper>
            )}
            {image.imageType === "360" && productSettings.imageProvider === "SIRV" && (
                <div style={{ minHeight: 300 }} onClick={imageWrapperClickHandler}>
                    <div className="Sirv" key={image.id} data-src={image.mediumImagePath} />
                </div>
            )}
            {/* XNG-Change: add zoom icon */}
            {onClick && (
                <Clickable
                    {...styles.enlargeBtn}
                    onClick={viewImage}
                    data-test-selector="productDetails-image-zoom"
                    aria-label="zoom icon"
                >
                    <Icon src={IconProdListingsZoom} size={16} color="common.ui5" />
                </Clickable>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(ProductPrimaryImage);
